import { useEffect, useState } from "react";
import { MDSCheckbox, MDSCheckboxGroup, MDSGridList, MDSTextField } from "@/mds";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  StyledExplanation,
  StyledFormattingContentLeftAddon,
  StyledFormattingContentRightAddon,
  StyledHeading,
  StyledSubExplanation,
  StyledSubTitle,
} from "@/pages/integrations/shared/integrations.styles";
import { MdsHorizontalDivider } from "@/design-system/components/divider";
import { MdsButton, MdsButtonSize, MdsButtonVariant } from "@/design-system/components/button";
import { useAppStore } from "@/store";
import {
  TextMessageIntegrationOrganizingMode,
  TextMessageIntegrationOrganizingInstructions,
} from "@/store/text-message-senders/types";
import { IntegrationsFormattingCollections } from "@/pages/integrations/shared/integrations-formatting-collections";

export const IntegrationsFormattingForTextMessage = observer(() => {
  const { store } = useAppStore();
  const {
    organizingOption,
    organizingInstructions,
    setOrganizingOption,
    setOrganizingInstructions,
    organizingManualCollections,
    setOrganizingManualCollections,
  } = store.textMessageSenders;

  const [localOrganizingInstructions, setLocalOrganizingInstructions] =
    useState<TextMessageIntegrationOrganizingInstructions>(organizingInstructions);

  useEffect(() => {
    setLocalOrganizingInstructions(organizingInstructions);
  }, [organizingInstructions]);

  // Compute show states from local instructions
  const showOrganizingCustomize = localOrganizingInstructions !== null;

  const isOrganizingSaveDisabled = localOrganizingInstructions === organizingInstructions;

  const showSpecificCollections = organizingOption.includes("manual");

  // Generate organizing option items
  const organizingItems = [
    {
      id: "specific-collections",
      label: "Specific collections",
      textValue: "Specific Collections",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSCheckbox value="manual" aria-label="Specific collections" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <OptionTitle>Specific Collections</OptionTitle>
          <OptionDescription>
            Always add notes created from text message to one or more collections
          </OptionDescription>
          {showSpecificCollections && (
            <ExtraContentContainer>
              <ExtraContent>
                <IntegrationsFormattingCollections
                  organizingManualCollections={organizingManualCollections}
                  setOrganizingManualCollections={setOrganizingManualCollections}
                />
              </ExtraContent>
            </ExtraContentContainer>
          )}
        </OptionLabel>
      ),
    },
    {
      id: "smart-organizing",
      label: "Smart organizing",
      textValue: "Smart",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSCheckbox value="smart" aria-label="Smart organizing" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <OptionTitle>Smart</OptionTitle>
          <OptionDescription>
            Allow Mem to pick the collections to organize each text message you save
          </OptionDescription>
          {showOrganizingCustomize && (
            <ExtraContentContainer>
              <MdsHorizontalDivider />
              <ExtraContent>
                <StyledSubTitle>Customize how text messages are organized</StyledSubTitle>
                <StyledSubExplanation>
                  Provide more guidance to how to organize notes from different kinds of text
                  messages
                </StyledSubExplanation>
                <StyledTextField
                  isTextArea
                  textAreaProps={{ rows: 3 }}
                  aria-label="Customize how text messages are organized"
                  value={localOrganizingInstructions}
                  onChange={setLocalOrganizingInstructions}
                  placeholder="For project updates, organize into your work projects collection. For article links, add to your reading list collection. For meal ideas, categorize in your recipe collection. For travel plans, group in your upcoming trips collection."
                  name="organizingInstructions"
                />
                <LeftStyles>
                  <MdsButton
                    label="Cancel"
                    size={MdsButtonSize.Small}
                    variant={MdsButtonVariant.Outlined}
                    onClick={() => {
                      // revert to local value
                      setLocalOrganizingInstructions(organizingInstructions);
                    }}
                  />
                  <MdsButton
                    label="Save Settings"
                    size={MdsButtonSize.Small}
                    variant={MdsButtonVariant.FilledDark}
                    onClick={() => setOrganizingInstructions(localOrganizingInstructions)}
                    isDisabled={isOrganizingSaveDisabled}
                  />
                </LeftStyles>
              </ExtraContent>
            </ExtraContentContainer>
          )}
        </OptionLabel>
      ),
      rightAddon: !showOrganizingCustomize && (
        <StyledFormattingContentRightAddon>
          <MdsButton
            label="Customize"
            size={MdsButtonSize.Small}
            variant={MdsButtonVariant.Outlined}
            onClick={() => setLocalOrganizingInstructions("")}
          />
        </StyledFormattingContentRightAddon>
      ),
    },
  ];

  return (
    <Container>
      <Section>
        <StyledHeading>Organizing</StyledHeading>
        <StyledExplanation>
          Mem can automatically organize notes created from your text messages
        </StyledExplanation>

        <MDSCheckboxGroup
          aria-label="Organizing"
          value={organizingOption}
          onChange={value => {
            const typedValue = value as TextMessageIntegrationOrganizingMode[];
            setOrganizingOption(typedValue);

            if (!typedValue.includes("smart")) {
              setLocalOrganizingInstructions(null);
            }
          }}
        >
          <MDSGridList items={organizingItems} disallowTypeAhead />
        </MDSCheckboxGroup>
      </Section>
    </Container>
  );
});

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  position: relative;
  overflow: visible;
  padding-top: ${({ theme }) => theme.spacing.sm};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const OptionLabel = styled.div`
  display: flex;
  flex-direction: column;

  gap: ${({ theme }) => theme.spacing.xs};
`;

const OptionTitle = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

const OptionDescription = styled.div`
  color: ${({ theme }) => theme.colors.grey.x500};
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
`;

const ExtraContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
`;

const ExtraContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
  position: relative;
  transform-origin: top;
  transition: transform 0.2s ease-in-out;
`;

export const StyledTextField = styled(MDSTextField)`
  margin-top: ${({ theme }) => theme.spacing.sm};

  textarea {
    font-size: ${({ theme }) => theme.fontSizes.small};
  }
`;

const LeftStyles = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;
